// src/components/ui/EmptyState.tsx
//
// "Nothing here yet" pattern for lists / cards / pages that have no data.
// Preline-aligned: centered lucide icon (muted), heading (semibold ink-900),
// optional description (ink-500), optional CTA button.
//
// Replaces the inline "rounded-xl border border-dashed bg-background/40
// p-4 text-center text-sm text-ink-500" pattern scattered around the
// codebase. Use inside cards (UpcomingPaymentsCard / AlertsBanner drawer)
// or at the page level.

import * as React from 'react';
import { cn } from '@/lib/utils';

export interface EmptyStateProps {
  /** Lucide icon component or pre-rendered ReactNode (rendered at h-10 w-10
   *  text-ink-300 inside a soft-tinted circle). */
  icon: React.ReactNode;
  title: string;
  description?: string;
  /** Optional primary CTA button. Pass a fully rendered button (so caller
   *  chooses Link vs button / variant / loading state). */
  action?: React.ReactNode;
  className?: string;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 p-8 text-center',
        className,
      )}
    >
      <div
        className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-ink-500"
        aria-hidden
      >
        {icon}
      </div>
      <div className="space-y-1">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {description && (
          <p className="text-sm text-ink-500 max-w-sm">{description}</p>
        )}
      </div>
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}
